class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems = this.closest("cart-items");
      if (!cartItems) return;
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    this.cartUpdateAbortController?.abort();
  }

  resetQuantityInput(id) {
    resetQuantityInput(this, id);
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (isNaN(inputValue)) {
       return;
    }

    const min = parseInt(event.target.dataset.min) || 0;
    const max = parseInt(event.target.max) || Infinity;
    const step = parseInt(event.target.step) || 1;

    const { valid, message } = validateQuantityInput(inputValue, { min, max, step });

    if (!valid) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute("name"),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    this.cartUpdateAbortController?.abort();
    this.cartUpdateAbortController = new AbortController();

    fetch(`${routes.cart_url}?section_id=main-cart-items`, {
      signal: this.cartUpdateAbortController.signal,
    })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(
          responseText,
          "text/html"
        );
        const sourceQty = html.querySelector("cart-items");
        if (sourceQty) {
          this.innerHTML = sourceQty.innerHTML;
        }
      })
      .catch((e) => {
        if (e?.name === "AbortError") return;
        console.error(e);
        this.updateLiveRegions(0, window.cartStrings.error);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items")?.dataset?.id,
        selector: ".js-contents",
      },
      ...getCommonCartSections(),
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer")?.dataset?.id,
        selector: ".js-contents",
      },
    ].filter((s) => s.section);
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute("value");
          }
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.renderCartSections(parsedState);
        this.handleQuantityError(parsedState, line, items, quantityElement);
        this.manageFocusAfterUpdate(parsedState, line, name);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch((error) => {
        console.error("Cart quantity update failed:", error);
        this.querySelectorAll(".loading__spinner").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors");
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  /**
   * Updates is-empty state and re-renders all cart sections from parsed state.
   * @param {Object} parsedState - Parsed cart change response
   */
  renderCartSections(parsedState) {
    this.classList.toggle("is-empty", parsedState.item_count === 0);
    const cartFooter = document.getElementById("main-cart-footer");

    if (cartFooter)
      cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);

    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document
          .getElementById(section.id)
          ?.querySelector(section.selector) ||
        document.getElementById(section.id);

      if (elementToReplace) {
        elementToReplace.innerHTML = getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      }
    });
  }

  /**
   * Builds the error message (if any) for when the actual updated quantity
   * differs from the requested quantity, and calls updateLiveRegions.
   * @param {Object} parsedState - Parsed cart change response
   * @param {number} line - Line item index
   * @param {NodeList} items - Current cart item elements before update
   * @param {Element} quantityElement - The quantity input element
   */
  handleQuantityError(parsedState, line, items, quantityElement) {
    const updatedValue = parsedState.items[line - 1]
      ? parsedState.items[line - 1].quantity
      : undefined;
    let message = "";
    if (
      quantityElement &&
      items.length === parsedState.items.length &&
      updatedValue !== parseInt(quantityElement.value)
    ) {
      if (typeof updatedValue === "undefined") {
        message = window.cartStrings.error;
      } else {
        message = window.cartStrings.quantityError.replace(
          "[quantity]",
          updatedValue
        );
      }
    }
    this.updateLiveRegions(line, message);
  }

  /**
   * Manages focus after a cart quantity update for accessibility.
   * @param {Object} parsedState - Parsed cart change response
   * @param {number} line - Line item index
   * @param {string} name - The name attribute of the quantity input
   */
  manageFocusAfterUpdate(parsedState, line, name) {
    const lineItem = document.getElementById(`CartItem-${line}`);
    if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
      lineItem.querySelector(`[name="${name}"]`).focus();
    }
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").textContent =
        message;

    this.lineItemStatusElement?.setAttribute("aria-hidden", true);

    const cartStatusElement =
      document.getElementById("cart-live-region-text");
    cartStatusElement?.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatusElement?.setAttribute("aria-hidden", true);
    }, CART_NOTE_SAVE_DELAY_MS);
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items");
    mainCartItems?.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );

    [...cartItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement?.blur();
    this.lineItemStatusElement?.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items");
    mainCartItems?.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            }).catch((error) => {
              console.error("Cart note update failed:", error);
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
